import type { AplMaterial } from '../types'
import SvarsutrymmeVy from './SvarsutrymmeVy'
import UtskriftPortal, { useUtskrift } from './UtskriftPortal'

const TYP_ETIKETT: Record<AplMaterial['typ'], string> = {
  observationsmall: 'Observationsmall',
  protokoll: 'Protokoll',
  checklista: 'Checklista',
  frageformular: 'Frågeformulär',
  jamforelsemall: 'Jämförelsemall',
}

/** Materialets innehåll — samma komponent i förhandsvisningen, i utskriften
 *  OCH i hela-läromedlet-utskriften (SkrivUtAlla), så att alla tre visar exakt
 *  det som kommer ur skrivaren. Tabelldelen (kolumner/rader/exempelrad)
 *  utelämnas helt om materialet inte har några kolumner — ett protokoll kan
 *  bestå av bara öppna fält. */
export function AplMaterialInnehall({ material }: { material: AplMaterial }) {
  return (
    <>
      {material.instruktion && <p className="apl-material-instruktion">{material.instruktion}</p>}

      {material.kolumner.length > 0 && (
        <table className="apl-material-tabell">
          <thead>
            <tr>
              {material.kolumner.map((kolumn) => (
                <th key={kolumn}>{kolumn}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {material.exempelrad.length > 0 && (
              <tr className="apl-material-exempelrad" aria-label="Exempel">
                {material.exempelrad.map((cell, i) => (
                  <td key={i}>
                    {i === 0 && <span className="apl-material-exempeletikett">Exempel</span>}
                    {cell}
                  </td>
                ))}
              </tr>
            )}
            {/* Tomma rader eleven fyller i själv — samma antal vid skärm och utskrift. */}
            {Array.from({ length: material.rader ?? 0 }, (_, rad) => (
              <tr key={rad}>
                {material.kolumner.map((_, kolumn) => (
                  <td key={kolumn} />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {material.falt.map((falt, i) => (
        <div key={i} className="apl-material-falt">
          <p className="apl-material-faltrubrik">{falt.rubrik}</p>
          {falt.hjalptext && <p className="apl-material-hjalptext">{falt.hjalptext}</p>}
          <SvarsutrymmeVy svarsutrymme={{ typ: 'rader', antal: falt.rader ?? 3 }} />
        </div>
      ))}

      {material.fotnot && <p className="apl-material-fotnot">{material.fotnot}</p>}
    </>
  )
}

/** Ett färdigt APL-material: kompakt rad med rubrik, typ och en Skriv ut-knapp,
 *  innehållet bakom en förhandsvisning så att uppgiftskortet inte växer till en
 *  textvägg. Utskriften går genom UtskriftPortal (samma fokusutskrift utan
 *  webbkrom som arbetsblad och lektionernas elevmaterial). */
export default function AplMaterialVy({ material }: { material: AplMaterial }) {
  const utskrift = useUtskrift()

  return (
    <div className="apl-material">
      <div className="apl-material-huvud">
        <span className="apl-material-typ">{TYP_ETIKETT[material.typ] ?? material.typ}</span>
        <h4>{material.rubrik}</h4>
        <button type="button" className="knapp knapp-sekundar knapp-liten no-print" onClick={utskrift.skrivUt}>
          🖨 Skriv ut
        </button>
      </div>

      <details className="apl-material-forhandsvisning">
        <summary>Förhandsgranska materialet</summary>
        <div className="apl-material-innehall">
          <AplMaterialInnehall material={material} />
        </div>
      </details>

      {utskrift.aktiv && (
        <UtskriftPortal onKlar={utskrift.avsluta}>
          <div className="apl-material-utskrift">
            <h1 className="arbetsblad-rubrik">{material.rubrik}</h1>
            <p className="arbetsblad-namnrad">Namn: _______________________ Klass: _________ Datum: _________</p>
            <AplMaterialInnehall material={material} />
          </div>
        </UtskriftPortal>
      )}
    </div>
  )
}
